import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH PRODUCTS
export const fetchAllProducts = createAsyncThunk(
  "products/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/products", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customers",
      );
    }
  },
);

// FETCH PRODUCT STATS (summary cards)
export const fetchProductStats = createAsyncThunk(
  "products/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/products/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch product stats",
      );
    }
  },
);

// FETCH PRODUCT LIST
export const fetchProductList = createAsyncThunk(
  "products/productList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/products/product-list", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch product list",
      );
    }
  },
);


// CREATE PRODUCT
export const addProduct = createAsyncThunk(
  "products/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/products", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// UPDATE PRODUCT
export const updateProduct = createAsyncThunk(
  "products/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      payload.append("_method", "PUT");

      const res = await Axios.post(`/tenant/products/${id}`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// FETCH SINGLE PRODUCT
export const getProductById = createAsyncThunk(
  "products/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/products/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch customer",
      );
    }
  },
);

// DELETE PRODUCT BY ID
export const deleteProduct = createAsyncThunk(
  "products/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/products/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete customer",
      );
    }
  },
);

// CHANGE PRODUCT STATUS
export const changeProductStatus = createAsyncThunk(
  "products/changeStatus",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/products/${id}/change-status`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to change status");
    }
  },
);

// BULK DELETE PRODUCTS
export const bulkDeleteProducts = createAsyncThunk(
  "products/bulkDelete",
  async (productIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/products/bulk-delete`, {
        data: { ids: productIds },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);


export const previewProductPdf = createAsyncThunk(
  "products/previewPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/fast-product-preview", payload, {
        responseType: "blob",
      });

      let blob = response.data;
      let pdf_url = null;
      let pdf_base64 = null;

      // Smart Handling: JSON or PDF
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        pdf_url = json.data?.pdf_url || json.pdf_url;
        pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

        if (pdf_base64) {
          const byteCharacters = atob(pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (pdf_url) {
          const pdfResponse = await fetch(pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob.type !== "application/pdf") {
        throw new Error("Failed to load PDF content");
      }

      return { blob, pdf_url, pdf_base64 }; // Standardized return
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to generate preview",
      );
    }
  },
);

const productsSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    productList: [],
    total_records: 0,
    isLoading: false,
    error: null,
    isBulkDeleting: false,
    stats: null,
    isStatsLoading: false,
  },

  extraReducers: (builder) => {
    builder
      // PRODUCT STATS
      .addCase(fetchProductStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchProductStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchProductStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      // FETCH ALL PRODUCTS
      .addCase(fetchAllProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch products";
      })

      //FETCH PRODUCT LIST
      .addCase(fetchProductList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchProductList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch product list";
      })
  },
});

export default productsSlice.reducer;
